import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { History, Pause, Play, Loader2, Moon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Bar = ({ label, part }) => (
  <div className="flex items-center gap-2 text-[11px]">
    <span className="w-16 text-muted-foreground">{label}</span>
    {part.completed ? <span className="text-green-700">Concluída</span> : (
      <><Progress value={part.progress} className="h-1.5 flex-1" /><span className="w-8 text-right font-mono">{part.progress}%</span></>
    )}
  </div>
);

const ProjectRow = ({ p }) => (
  <li className="py-2 space-y-1" data-testid="reimports-project-row">
    <p className="text-xs font-medium truncate">{p.name}</p>
    {p.auth_blocked ? <p className="text-[11px] text-amber-700">Token da Wake inválido: reimportação em espera</p>
      : p.waiting_history ? <p className="text-[11px] text-muted-foreground">Aguardando a importação do histórico</p>
        : <><Bar label="Logística" part={p.logistics} /><Bar label="Itens" part={p.items} /></>}
  </li>
);

// Admin-only control in the top bar; renders nothing for non-admins (403).
export const ReimportsControl = () => {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => axios.get(`${API_URL}/api/health/jobs/reimports`).then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const toggle = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/health/jobs/reimports`, { paused: !d.paused });
      toast.success(d.paused ? 'Reimportações retomadas (rodam das 22h às 7h)' : 'Reimportações pausadas');
      await load();
    } catch (e) { toast.error('Não foi possível alterar as reimportações'); }
    finally { setSaving(false); }
  };
  const state = d.paused ? 'Pausadas' : d.window_open ? 'Rodando' : 'Aguardando 22h';
  const pending = d.projects.filter((p) => !(p.logistics.completed && p.items.completed));
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-background/50 text-xs hover:bg-muted" data-testid="reimports-trigger">
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">Reimportações:</span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${d.paused ? 'bg-gray-100 text-gray-600' : d.window_open ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`} data-testid="reimports-state">{state}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="reimports-popover">
        <p className="text-sm font-medium">Reimportação de itens e logística</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Moon className="h-3 w-3" />Roda só das {d.window} (horário de Brasília), uma tarefa por vez.</p>
        <Button size="sm" className="w-full mt-3" variant={d.paused ? 'default' : 'outline'} onClick={toggle} disabled={saving} data-testid="reimports-toggle">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : d.paused ? <Play className="h-3.5 w-3.5 mr-1" /> : <Pause className="h-3.5 w-3.5 mr-1" />}
          {d.paused ? 'Retomar reimportações' : 'Pausar reimportações'}
        </Button>
        {d.changed_by && <p className="text-[11px] text-muted-foreground mt-1">Alterado por {d.changed_by} em {new Date(d.changed_at).toLocaleString('pt-BR')}</p>}
        <p className="text-xs text-muted-foreground mt-3">{pending.length ? `${pending.length} de ${d.projects.length} projetos com reimportação pendente` : 'Todas as reimportações concluídas.'}</p>
        <ul className="divide-y max-h-72 overflow-y-auto" data-testid="reimports-projects">
          {d.projects.map((p) => <ProjectRow key={p.project_id} p={p} />)}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
